<template>
  <div>
    <!-- 导航栏 -->
    <Breadcrumb />
    <Navbar />

    <div class="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
      <!-- Hero Section -->
      <section class="relative py-20 px-4 overflow-hidden">
        <!-- 动态背景 -->
        <DynamicBackground variant="product" :show-particles="false" :show-streaming-light="true" />
        <div class="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]"></div>

        <div class="container mx-auto max-w-6xl relative z-10">
          <div class="text-center mb-16">
            <div class="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                class="text-primary">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
                <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
                <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
              </svg>
              <span class="text-sm font-medium text-primary">企业级软件定制开发</span>
            </div>

            <h1
              class="text-4xl md:text-6xl font-bold mb-6 leading-tight bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent animate-scale">
              数字化转型 从这里开始
            </h1>

            <p class="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
              专业的企业级软件定制开发服务，涵盖ERP、MES、进销存、办公软件等全方位解决方案，
              助力企业实现数字化转型升级，提升运营效率和竞争力。
            </p>

            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg"
                class="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"
                    stroke="currentColor" stroke-width="2" />
                  <polyline points="3.27,6.96 12,12.01 20.73,6.96" stroke="currentColor" stroke-width="2" />
                  <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" stroke-width="2" />
                </svg>
                立即咨询定制方案
              </Button>

              <Button variant="outline" size="lg"
                class="border-primary/20 hover:border-primary hover:bg-primary/5 transition-all duration-300 hover:scale-105 flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <polygon points="5,3 19,12 5,21" stroke="currentColor" stroke-width="2" fill="currentColor" />
                </svg>
                观看演示视频
              </Button>
            </div>
          </div>

          <!-- 核心数据展示 -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            <div
              class="text-center p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div class="text-3xl font-bold text-primary mb-2">200+</div>
              <div class="text-muted-foreground">成功案例</div>
            </div>
            <div
              class="text-center p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div class="text-3xl font-bold text-primary mb-2">15+</div>
              <div class="text-muted-foreground">行业领域</div>
            </div>
            <div
              class="text-center p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div class="text-3xl font-bold text-primary mb-2">98%</div>
              <div class="text-muted-foreground">客户满意度</div>
            </div>
            <div
              class="text-center p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div class="text-3xl font-bold text-primary mb-2">7×24</div>
              <div class="text-muted-foreground">技术支持</div>
            </div>
          </div>
        </div>
      </section>

      <!-- 核心产品模块 -->
      <section class="py-20 px-4">
        <div class="container mx-auto max-w-6xl">
          <div class="text-center mb-16">
            <h2 class="text-3xl md:text-4xl font-bold mb-4">核心产品模块</h2>
            <p class="text-xl text-muted-foreground max-w-2xl mx-auto">
              全面覆盖企业运营各个环节，打造一体化数字生态系统
            </p>
          </div>

          <div class="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            <!-- ERP系统 -->
            <div
              class="group relative p-8 rounded-3xl bg-gradient-to-br from-card via-card to-primary/5 border border-border/50 hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] overflow-hidden">
              <!-- 背景装饰 -->
              <div
                class="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700">
              </div>
              <div
                class="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-full blur-xl group-hover:scale-125 transition-transform duration-700">
              </div>

              <div class="relative z-10">
                <div class="flex items-center gap-4 mb-6">
                  <div class="p-3 rounded-2xl bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                      class="text-primary">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" stroke-width="2" />
                      <line x1="9" y1="9" x2="9" y2="15" stroke="currentColor" stroke-width="2" />
                      <line x1="15" y1="9" x2="15" y2="15" stroke="currentColor" stroke-width="2" />
                      <line x1="9" y1="12" x2="15" y2="12" stroke="currentColor" stroke-width="2" />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-2xl font-bold mb-1">ERP企业资源计划系统</h3>
                    <p class="text-primary font-medium">Enterprise Resource Planning</p>
                  </div>
                </div>

                <p class="text-muted-foreground mb-6 leading-relaxed">
                  集成财务管理、供应链管理、人力资源、客户关系等核心业务模块，融合AI智能分析、自动化审批、预测性决策支持，实现企业资源的智能化统一规划和优化配置。
                </p>

                <div class="grid grid-cols-2 gap-4 mb-6">
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-primary"></div>
                    <span>AI财务风险预警</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-primary"></div>
                    <span>智能供应链优化</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-primary"></div>
                    <span>AI人才匹配推荐</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-primary"></div>
                    <span>智能客户画像分析</span>
                  </div>
                </div>

                <Button variant="outline"
                  class="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                  了解详情
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="ml-2 group-hover:translate-x-1 transition-transform duration-200">
                    <path d="m9 18 6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </Button>
              </div>
            </div>

            <!-- MES系统 -->
            <div
              class="group relative p-8 rounded-3xl bg-gradient-to-br from-card via-card to-blue-500/5 border border-border/50 hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] overflow-hidden">
              <div
                class="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-500/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700">
              </div>
              <div
                class="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-primary/10 to-transparent rounded-full blur-xl group-hover:scale-125 transition-transform duration-700">
              </div>

              <div class="relative z-10">
                <div class="flex items-center gap-4 mb-6">
                  <div class="p-3 rounded-2xl bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors duration-300">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                      class="text-blue-500">
                      <path
                        d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
                        stroke="currentColor" stroke-width="2" />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-2xl font-bold mb-1">MES制造执行系统</h3>
                    <p class="text-blue-500 font-medium">Manufacturing Execution System</p>
                  </div>
                </div>

                <p class="text-muted-foreground mb-6 leading-relaxed">
                  连接计划层和车间层的信息纽带，融合AI视觉检测、预测性维护、智能排产算法，实现生产过程的智能监控、质量追溯和设备管理的全面数字化。
                </p>

                <div class="grid grid-cols-2 gap-4 mb-6">
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span>AI智能排产调度</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span>AI视觉质量检测</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span>预测性设备维护</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span>实时数据智能分析</span>
                  </div>
                </div>

                <Button variant="outline"
                  class="w-full group-hover:bg-blue-500 group-hover:text-white transition-all duration-300 border-blue-500/20 hover:border-blue-500">
                  了解详情
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="ml-2 group-hover:translate-x-1 transition-transform duration-200">
                    <path d="m9 18 6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </Button>
              </div>
            </div>

            <!-- 进销存系统 -->
            <div
              class="group relative p-8 rounded-3xl bg-gradient-to-br from-card via-card to-green-500/5 border border-border/50 hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] overflow-hidden">
              <div
                class="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-green-500/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700">
              </div>
              <div
                class="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-primary/10 to-transparent rounded-full blur-xl group-hover:scale-125 transition-transform duration-700">
              </div>

              <div class="relative z-10">
                <div class="flex items-center gap-4 mb-6">
                  <div
                    class="p-3 rounded-2xl bg-green-500/10 group-hover:bg-green-500/20 transition-colors duration-300">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                      class="text-green-500">
                      <path d="M20 7h-9" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                      <path d="M14 3v4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                      <rect x="2" y="3" width="5" height="5" rx="1" stroke="currentColor" stroke-width="2" />
                      <path d="M20 15h-9" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                      <path d="M14 11v4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                      <rect x="2" y="11" width="5" height="5" rx="1" stroke="currentColor" stroke-width="2" />
                      <path d="M20 7v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z"
                        stroke="currentColor" stroke-width="2" />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-2xl font-bold mb-1">进销存管理系统</h3>
                    <p class="text-green-500 font-medium">Inventory Management System</p>
                  </div>
                </div>

                <p class="text-muted-foreground mb-6 leading-relaxed">
                  全面管控商品采购、销售、库存流程，集成AI需求预测、智能补货、价格优化算法，实现精准库存控制和智能决策，降低运营成本，提高资金周转效率。
                </p>

                <div class="grid grid-cols-2 gap-4 mb-6">
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-green-500"></div>
                    <span>AI智能采购推荐</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-green-500"></div>
                    <span>销售趋势预测分析</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-green-500"></div>
                    <span>智能库存优化</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-green-500"></div>
                    <span>自动财务对账</span>
                  </div>
                </div>

                <Button variant="outline"
                  class="w-full group-hover:bg-green-500 group-hover:text-white transition-all duration-300 border-green-500/20 hover:border-green-500">
                  了解详情
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="ml-2 group-hover:translate-x-1 transition-transform duration-200">
                    <path d="m9 18 6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </Button>
              </div>
            </div>

            <!-- 办公软件系统 -->
            <div
              class="group relative p-8 rounded-3xl bg-gradient-to-br from-card via-card to-purple-500/5 border border-border/50 hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] overflow-hidden">
              <div
                class="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-500/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700">
              </div>
              <div
                class="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-primary/10 to-transparent rounded-full blur-xl group-hover:scale-125 transition-transform duration-700">
              </div>

              <div class="relative z-10">
                <div class="flex items-center gap-4 mb-6">
                  <div
                    class="p-3 rounded-2xl bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors duration-300">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                      class="text-purple-500">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" stroke-width="2" />
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" stroke-width="2" />
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" stroke-width="2" />
                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" stroke-width="2" />
                      <path d="M8 14h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                      <path d="M12 14h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                      <path d="M16 14h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                      <path d="M8 18h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                      <path d="M12 18h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-2xl font-bold mb-1">智能办公系统</h3>
                    <p class="text-purple-500 font-medium">Office Automation System</p>
                  </div>
                </div>

                <p class="text-muted-foreground mb-6 leading-relaxed">
                  集成协同办公、项目管理、知识管理等功能，融合AI智能助手、语音转文字、智能会议纪要、文档自动生成，打造高效便捷的智能化办公环境，显著提升团队协作效率。
                </p>

                <div class="grid grid-cols-2 gap-4 mb-6">
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span>AI智能协同办公</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span>智能项目进度预测</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span>智能知识检索</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <div class="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span>智能流程审批</span>
                  </div>
                </div>

                <Button variant="outline"
                  class="w-full group-hover:bg-purple-500 group-hover:text-white transition-all duration-300 border-purple-500/20 hover:border-purple-500">
                  了解详情
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="ml-2 group-hover:translate-x-1 transition-transform duration-200">
                    <path d="m9 18 6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- AI智能增强 -->
      <section class="py-20 px-4 bg-gradient-to-br from-primary/5 via-background to-blue-500/5">
        <div class="container mx-auto max-w-6xl">
          <div class="text-center mb-16">
            <div
              class="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/10 to-blue-500/10 rounded-full mb-6">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                class="text-primary">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
                <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
                <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
              </svg>
              <span
                class="text-sm font-medium bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent">AI智能赋能</span>
            </div>
            <h2 class="text-3xl md:text-4xl font-bold mb-4">AI驱动的智能化升级</h2>
            <p class="text-xl text-muted-foreground max-w-3xl mx-auto">
              集成前沿人工智能技术，让传统企业软件焕发全新智能，提升效率、降低成本、增强决策能力
            </p>
          </div>

          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <!-- 智能文档处理 -->
            <div
              class="group relative p-6 rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 hover:shadow-xl transition-all duration-500 hover:scale-[1.02] overflow-hidden">
              <div
                class="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-primary/20 to-transparent rounded-full blur-xl group-hover:scale-150 transition-transform duration-700">
              </div>
              <div class="relative z-10">
                <div
                  class="w-12 h-12 mb-4 p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="text-primary">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor"
                      stroke-width="2" />
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" stroke-width="2" />
                    <path d="M16 13H8M16 17H8M10 9H8" stroke="currentColor" stroke-width="2" />
                  </svg>
                </div>
                <h3 class="text-lg font-bold mb-3">智能文档处理</h3>
                <p class="text-muted-foreground text-sm mb-4 leading-relaxed">
                  OCR智能识别、图片转Excel、发票自动录入、合同智能审查，告别繁琐的手工录入
                </p>
                <div class="flex flex-wrap gap-2">
                  <span class="px-2 py-1 text-xs bg-primary/10 text-primary rounded-md">OCR识别</span>
                  <span class="px-2 py-1 text-xs bg-primary/10 text-primary rounded-md">智能提取</span>
                  <span class="px-2 py-1 text-xs bg-primary/10 text-primary rounded-md">自动分类</span>
                </div>
              </div>
            </div>

            <!-- 智能工作流 -->
            <div
              class="group relative p-6 rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 hover:shadow-xl transition-all duration-500 hover:scale-[1.02] overflow-hidden">
              <div
                class="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-blue-500/20 to-transparent rounded-full blur-xl group-hover:scale-150 transition-transform duration-700">
              </div>
              <div class="relative z-10">
                <div
                  class="w-12 h-12 mb-4 p-3 rounded-xl bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors duration-300">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="text-blue-500">
                    <path
                      d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"
                      stroke="currentColor" stroke-width="2" />
                    <polyline points="3.27,6.96 12,12.01 20.73,6.96" stroke="currentColor" stroke-width="2" />
                    <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" stroke-width="2" />
                  </svg>
                </div>
                <h3 class="text-lg font-bold mb-3">智能工作流引擎</h3>
                <p class="text-muted-foreground text-sm mb-4 leading-relaxed">
                  RPA自动化、流程智能优化、异常自动预警、多系统数据同步，让工作流更智能
                </p>
                <div class="flex flex-wrap gap-2">
                  <span class="px-2 py-1 text-xs bg-blue-500/10 text-blue-500 rounded-md">RPA自动化</span>
                  <span class="px-2 py-1 text-xs bg-blue-500/10 text-blue-500 rounded-md">流程优化</span>
                  <span class="px-2 py-1 text-xs bg-blue-500/10 text-blue-500 rounded-md">智能调度</span>
                </div>
              </div>
            </div>

            <!-- 智能语音助手 -->
            <div
              class="group relative p-6 rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 hover:shadow-xl transition-all duration-500 hover:scale-[1.02] overflow-hidden">
              <div
                class="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-green-500/20 to-transparent rounded-full blur-xl group-hover:scale-150 transition-transform duration-700">
              </div>
              <div class="relative z-10">
                <div
                  class="w-12 h-12 mb-4 p-3 rounded-xl bg-green-500/10 group-hover:bg-green-500/20 transition-colors duration-300">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="text-green-500">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" stroke="currentColor"
                      stroke-width="2" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" stroke-width="2" />
                    <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" stroke-width="2" />
                    <line x1="8" y1="23" x2="16" y2="23" stroke="currentColor" stroke-width="2" />
                  </svg>
                </div>
                <h3 class="text-lg font-bold mb-3">AI语音助手</h3>
                <p class="text-muted-foreground text-sm mb-4 leading-relaxed">
                  语音操作系统、智能会议记录、多语言翻译、语音转文字，解放双手提升效率
                </p>
                <div class="flex flex-wrap gap-2">
                  <span class="px-2 py-1 text-xs bg-green-500/10 text-green-500 rounded-md">语音识别</span>
                  <span class="px-2 py-1 text-xs bg-green-500/10 text-green-500 rounded-md">智能对话</span>
                  <span class="px-2 py-1 text-xs bg-green-500/10 text-green-500 rounded-md">多语言</span>
                </div>
              </div>
            </div>

            <!-- 智能数据分析 -->
            <div
              class="group relative p-6 rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 hover:shadow-xl transition-all duration-500 hover:scale-[1.02] overflow-hidden">
              <div
                class="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-purple-500/20 to-transparent rounded-full blur-xl group-hover:scale-150 transition-transform duration-700">
              </div>
              <div class="relative z-10">
                <div
                  class="w-12 h-12 mb-4 p-3 rounded-xl bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors duration-300">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="text-purple-500">
                    <line x1="18" y1="20" x2="18" y2="10" stroke="currentColor" stroke-width="2" />
                    <line x1="12" y1="20" x2="12" y2="4" stroke="currentColor" stroke-width="2" />
                    <line x1="6" y1="20" x2="6" y2="14" stroke="currentColor" stroke-width="2" />
                  </svg>
                </div>
                <h3 class="text-lg font-bold mb-3">智能数据洞察</h3>
                <p class="text-muted-foreground text-sm mb-4 leading-relaxed">
                  销售预测、库存优化、风险预警、趋势分析，让数据为决策赋能
                </p>
                <div class="flex flex-wrap gap-2">
                  <span class="px-2 py-1 text-xs bg-purple-500/10 text-purple-500 rounded-md">预测分析</span>
                  <span class="px-2 py-1 text-xs bg-purple-500/10 text-purple-500 rounded-md">风险识别</span>
                  <span class="px-2 py-1 text-xs bg-purple-500/10 text-purple-500 rounded-md">智能推荐</span>
                </div>
              </div>
            </div>

            <!-- 智能客服 -->
            <div
              class="group relative p-6 rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 hover:shadow-xl transition-all duration-500 hover:scale-[1.02] overflow-hidden">
              <div
                class="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-orange-500/20 to-transparent rounded-full blur-xl group-hover:scale-150 transition-transform duration-700">
              </div>
              <div class="relative z-10">
                <div
                  class="w-12 h-12 mb-4 p-3 rounded-xl bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors duration-300">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="text-orange-500">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor"
                      stroke-width="2" />
                    <path d="M13 8H8M16 12H8" stroke="currentColor" stroke-width="2" />
                  </svg>
                </div>
                <h3 class="text-lg font-bold mb-3">AI智能客服</h3>
                <p class="text-muted-foreground text-sm mb-4 leading-relaxed">
                  7×24小时在线、情感分析、智能回复、知识库检索，提升客户满意度
                </p>
                <div class="flex flex-wrap gap-2">
                  <span class="px-2 py-1 text-xs bg-orange-500/10 text-orange-500 rounded-md">智能问答</span>
                  <span class="px-2 py-1 text-xs bg-orange-500/10 text-orange-500 rounded-md">情感识别</span>
                  <span class="px-2 py-1 text-xs bg-orange-500/10 text-orange-500 rounded-md">多渠道</span>
                </div>
              </div>
            </div>

            <!-- 预测性维护 -->
            <div
              class="group relative p-6 rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 hover:shadow-xl transition-all duration-500 hover:scale-[1.02] overflow-hidden">
              <div
                class="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-pink-500/20 to-transparent rounded-full blur-xl group-hover:scale-150 transition-transform duration-700">
              </div>
              <div class="relative z-10">
                <div
                  class="w-12 h-12 mb-4 p-3 rounded-xl bg-pink-500/10 group-hover:bg-pink-500/20 transition-colors duration-300">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="text-pink-500">
                    <path
                      d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
                      stroke="currentColor" stroke-width="2" />
                  </svg>
                </div>
                <h3 class="text-lg font-bold mb-3">智能运维监控</h3>
                <p class="text-muted-foreground text-sm mb-4 leading-relaxed">
                  设备预测性维护、性能监控、故障诊断、自动报警，保障系统稳定运行
                </p>
                <div class="flex flex-wrap gap-2">
                  <span class="px-2 py-1 text-xs bg-pink-500/10 text-pink-500 rounded-md">故障预测</span>
                  <span class="px-2 py-1 text-xs bg-pink-500/10 text-pink-500 rounded-md">性能优化</span>
                  <span class="px-2 py-1 text-xs bg-pink-500/10 text-pink-500 rounded-md">自动修复</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI集成方案 -->
          <div
            class="mt-16 p-8 rounded-3xl bg-gradient-to-r from-primary/5 via-blue-500/5 to-purple-500/5 border border-primary/20">
            <div class="text-center mb-8">
              <h3 class="text-2xl font-bold mb-4">深度学习模型集成</h3>
              <p class="text-muted-foreground max-w-3xl mx-auto">
                支持主流AI模型接入，包括GPT、Claude、DeepSeek等大语言模型，以及专业的图像识别、语音处理模型
              </p>
            </div>

            <div class="grid md:grid-cols-4 gap-6">
              <div class="text-center p-4 rounded-xl bg-card/50 border border-border/30">
                <div class="w-12 h-12 mx-auto mb-3 p-3 rounded-lg bg-primary/10">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="text-primary">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
                    <circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="2" />
                    <circle cx="12" cy="12" r="2" stroke="currentColor" stroke-width="2" />
                  </svg>
                </div>
                <h4 class="font-semibold mb-2">大语言模型</h4>
                <p class="text-sm text-muted-foreground">GPT、Claude、deepseek</p>
              </div>

              <div class="text-center p-4 rounded-xl bg-card/50 border border-border/30">
                <div class="w-12 h-12 mx-auto mb-3 p-3 rounded-lg bg-blue-500/10">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="text-blue-500">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" stroke-width="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" stroke-width="2" />
                    <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="2" />
                  </svg>
                </div>
                <h4 class="font-semibold mb-2">计算机视觉</h4>
                <p class="text-sm text-muted-foreground">OCR、图像分类、目标检测</p>
              </div>

              <div class="text-center p-4 rounded-xl bg-card/50 border border-border/30">
                <div class="w-12 h-12 mx-auto mb-3 p-3 rounded-lg bg-green-500/10">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="text-green-500">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" stroke="currentColor"
                      stroke-width="2" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" stroke-width="2" />
                  </svg>
                </div>
                <h4 class="font-semibold mb-2">语音识别</h4>
                <p class="text-sm text-muted-foreground">ASR、TTS、语音合成</p>
              </div>

              <div class="text-center p-4 rounded-xl bg-card/50 border border-border/30">
                <div class="w-12 h-12 mx-auto mb-3 p-3 rounded-lg bg-purple-500/10">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="text-purple-500">
                    <line x1="18" y1="20" x2="18" y2="10" stroke="currentColor" stroke-width="2" />
                    <line x1="12" y1="20" x2="12" y2="4" stroke="currentColor" stroke-width="2" />
                    <line x1="6" y1="20" x2="6" y2="14" stroke="currentColor" stroke-width="2" />
                  </svg>
                </div>
                <h4 class="font-semibold mb-2">机器学习</h4>
                <p class="text-sm text-muted-foreground">预测建模、异常检测</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 技术架构 -->
      <section class="py-20 px-4 bg-muted/20">
        <div class="container mx-auto max-w-6xl">
          <div class="text-center mb-16">
            <h2 class="text-3xl md:text-4xl font-bold mb-4">先进技术架构</h2>
            <p class="text-xl text-muted-foreground max-w-2xl mx-auto">
              基于云原生架构，采用微服务设计，确保系统的可扩展性、稳定性和安全性
            </p>
          </div>

          <div class="grid md:grid-cols-3 gap-8">
            <!-- 云原生架构 -->
            <div
              class="text-center p-8 rounded-2xl bg-card border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div class="w-16 h-16 mx-auto mb-6 p-4 rounded-2xl bg-primary/10">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="text-primary">
                  <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <h3 class="text-xl font-bold mb-4">云原生架构</h3>
              <p class="text-muted-foreground leading-relaxed">
                支持公有云、私有云、混合云部署，弹性扩容，高可用性保障
              </p>
            </div>

            <!-- 微服务设计 -->
            <div
              class="text-center p-8 rounded-2xl bg-card border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div class="w-16 h-16 mx-auto mb-6 p-4 rounded-2xl bg-blue-500/10">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="text-blue-500">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" />
                  <circle cx="12" cy="12" r="1" stroke="currentColor" stroke-width="2" />
                  <path d="M12 1v6m0 10v6" stroke="currentColor" stroke-width="2" />
                  <path d="m4.22 4.22 1.42 1.42m12.72 12.72 1.42 1.42" stroke="currentColor" stroke-width="2" />
                  <path d="M1 12h6m10 0h6" stroke="currentColor" stroke-width="2" />
                  <path d="m4.22 19.78 1.42-1.42m12.72-12.72 1.42-1.42" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <h3 class="text-xl font-bold mb-4">微服务设计</h3>
              <p class="text-muted-foreground leading-relaxed">
                模块化设计，独立部署，易于维护和升级，支持灵活定制
              </p>
            </div>

            <!-- 安全保障 -->
            <div
              class="text-center p-8 rounded-2xl bg-card border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div class="w-16 h-16 mx-auto mb-6 p-4 rounded-2xl bg-green-500/10">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="text-green-500">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" stroke-width="2" />
                  <path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <h3 class="text-xl font-bold mb-4">安全保障</h3>
              <p class="text-muted-foreground leading-relaxed">
                多重加密传输，权限分级管理，数据备份恢复，保障企业信息安全
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- 行业解决方案 -->
      <section class="py-20 px-4">
        <div class="container mx-auto max-w-6xl">
          <div class="text-center mb-16">
            <h2 class="text-3xl md:text-4xl font-bold mb-4">行业解决方案</h2>
            <p class="text-xl text-muted-foreground max-w-2xl mx-auto">
              深度融合行业特性，提供专业化定制开发服务
            </p>
          </div>

          <div class="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            <div
              class="p-6 rounded-xl bg-card border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105 text-center">
              <div class="w-12 h-12 mx-auto mb-4 p-3 rounded-lg bg-primary/10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="text-primary">
                  <path d="M3 21h18l-9-18-9 18z" stroke="currentColor" stroke-width="2" />
                  <path d="M12 9v4" stroke="currentColor" stroke-width="2" />
                  <path d="M12 17h.01" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <h4 class="font-semibold mb-2">制造业</h4>
              <p class="text-sm text-muted-foreground">智能制造解决方案</p>
            </div>

            <div
              class="p-6 rounded-xl bg-card border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105 text-center">
              <div class="w-12 h-12 mx-auto mb-4 p-3 rounded-lg bg-blue-500/10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="text-blue-500">
                  <path d="M3 21h18l-9-18-9 18z" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <h4 class="font-semibold mb-2">零售业</h4>
              <p class="text-sm text-muted-foreground">新零售数字化系统</p>
            </div>

            <div
              class="p-6 rounded-xl bg-card border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105 text-center">
              <div class="w-12 h-12 mx-auto mb-4 p-3 rounded-lg bg-green-500/10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="text-green-500">
                  <path d="M3 21h18l-9-18-9 18z" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <h4 class="font-semibold mb-2">物流业</h4>
              <p class="text-sm text-muted-foreground">智慧物流管理平台</p>
            </div>

            <div
              class="p-6 rounded-xl bg-card border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105 text-center">
              <div class="w-12 h-12 mx-auto mb-4 p-3 rounded-lg bg-purple-500/10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="text-purple-500">
                  <path d="M3 21h18l-9-18-9 18z" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <h4 class="font-semibold mb-2">医疗健康</h4>
              <p class="text-sm text-muted-foreground">医疗信息化系统</p>
            </div>

            <div
              class="p-6 rounded-xl bg-card border border-border/50 hover:shadow-lg transition-all duration-300 hover:scale-105 text-center">
              <div class="w-12 h-12 mx-auto mb-4 p-3 rounded-lg bg-orange-500/10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="text-orange-500">
                  <path d="M3 21h18l-9-18-9 18z" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <h4 class="font-semibold mb-2">教育培训</h4>
              <p class="text-sm text-muted-foreground">智慧教育管理平台</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 服务流程 -->
      <section class="py-20 px-4 bg-muted/20">
        <div class="container mx-auto max-w-6xl">
          <div class="text-center mb-16">
            <h2 class="text-3xl md:text-4xl font-bold mb-4">专业服务流程</h2>
            <p class="text-xl text-muted-foreground max-w-2xl mx-auto">
              从需求分析到交付上线，全程专业服务保障项目成功
            </p>
          </div>

          <div class="relative">
            <!-- 连接线 -->
            <div
              class="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-blue-500 to-primary transform -translate-y-1/2">
            </div>

            <div class="grid md:grid-cols-5 gap-8 relative z-10">
              <div class="text-center">
                <div class="w-16 h-16 mx-auto mb-4 bg-primary rounded-full flex items-center justify-center shadow-lg">
                  <span class="text-2xl font-bold text-primary-foreground">1</span>
                </div>
                <h4 class="font-semibold mb-2">需求调研</h4>
                <p class="text-sm text-muted-foreground">深入了解业务流程，精准把握需求</p>
              </div>

              <div class="text-center">
                <div class="w-16 h-16 mx-auto mb-4 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                  <span class="text-2xl font-bold text-white">2</span>
                </div>
                <h4 class="font-semibold mb-2">方案设计</h4>
                <p class="text-sm text-muted-foreground">制定技术方案，确定开发计划</p>
              </div>

              <div class="text-center">
                <div
                  class="w-16 h-16 mx-auto mb-4 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                  <span class="text-2xl font-bold text-white">3</span>
                </div>
                <h4 class="font-semibold mb-2">开发实施</h4>
                <p class="text-sm text-muted-foreground">敏捷开发，迭代交付，质量保证</p>
              </div>

              <div class="text-center">
                <div
                  class="w-16 h-16 mx-auto mb-4 bg-purple-500 rounded-full flex items-center justify-center shadow-lg">
                  <span class="text-2xl font-bold text-white">4</span>
                </div>
                <h4 class="font-semibold mb-2">测试部署</h4>
                <p class="text-sm text-muted-foreground">全面测试，安全部署，稳定上线</p>
              </div>

              <div class="text-center">
                <div
                  class="w-16 h-16 mx-auto mb-4 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                  <span class="text-2xl font-bold text-white">5</span>
                </div>
                <h4 class="font-semibold mb-2">运维支持</h4>
                <p class="text-sm text-muted-foreground">7×24技术支持，持续优化升级</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA区域 -->
      <section class="py-20 px-4">
        <div class="container mx-auto max-w-4xl text-center">
          <div
            class="relative p-12 rounded-3xl bg-gradient-to-br from-primary/5 via-background to-blue-500/5 border border-primary/20 overflow-hidden">
            <!-- 背景装饰 -->
            <div
              class="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-primary/20 to-transparent rounded-full blur-3xl">
            </div>
            <div
              class="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-500/15 to-transparent rounded-full blur-2xl">
            </div>

            <div class="relative z-10">
              <h2 class="text-3xl md:text-4xl font-bold mb-6">
                开启您的数字化转型之旅
              </h2>
              <p class="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
                专业的技术团队，丰富的行业经验，为您量身定制最适合的企业级软件解决方案
              </p>

              <div class="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg"
                  class="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="mr-2">
                    <path
                      d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                      stroke="currentColor" stroke-width="2" />
                  </svg>
                  免费咨询方案
                </Button>
                <Button variant="outline" size="lg"
                  class="border-primary/20 hover:border-primary hover:bg-primary/5 transition-all duration-300">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                    class="mr-2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor"
                      stroke-width="2" />
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" stroke-width="2" />
                    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" stroke-width="2" />
                    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" stroke-width="2" />
                    <polyline points="10,9 9,9 8,9" stroke="currentColor" stroke-width="2" />
                  </svg>
                  下载产品资料
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- 页脚 -->
    <Footer />
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar.vue';
import Breadcrumb from '@/components/Breadcrumb.vue';
import Footer from '@/components/Footer.vue';
import DynamicBackground from '@/components/animations/DynamicBackground.vue';

// 页面元数据
const pageTitle = '企业级软件定制开发 - 创诚纪元人工智能';
const pageDescription = '专业提供ERP、MES、进销存、办公软件等企业级应用定制开发服务，助力企业数字化转型升级。';

// 设置页面标题
if (typeof document !== 'undefined') {
  document.title = pageTitle;

  // 设置meta描述
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', pageDescription);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'description';
    meta.content = pageDescription;
    document.head.appendChild(meta);
  }
}
</script>

<style scoped>
/* 网格背景 */
.bg-grid-white\/\[0\.02\] {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' width='32' height='32' fill='none' stroke='rgb(255 255 255 / 0.02)'%3e%3cpath d='m0 .5h32m-32 32v-32'/%3e%3c/svg%3e");
}

/* 自定义动画 */
@keyframes float {

  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse-glow {

  0%,
  100% {
    box-shadow: 0 0 5px rgb(var(--primary) / 0.5);
  }

  50% {
    box-shadow: 0 0 20px rgb(var(--primary) / 0.8), 0 0 30px rgb(var(--primary) / 0.4);
  }
}

@keyframes scale-in {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-pulse-glow {
  animation: pulse-glow 2s ease-in-out infinite;
}

.animate-scale {
  animation: scale-in 0.8s ease-out 0.2s both;
}

.animate-scale:hover {
  opacity: 1 !important;
  color: inherit !important;
}

/* 响应式优化 */
@media (max-width: 768px) {
  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
